import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import Speedometer from "./Speedometer";
import { getBackgroundColor, atingiuMeta, getDirecaoMetas, getMetaAtingida, isIndicadorGatilhoAtivo } from "../utils";
import { IconAlert, InfoFormReflex } from "~misc/icons";
import Mock from "./../utils/mockup.json";
import TooltipReflex from "./TooltipReflex";
import useParametroReflex from "../hooks/useParametroReflex";
import { fmtPct, normalizePercentualValue } from "../utils/parseVolume";

const normalizarTipo = (tipo) => String(tipo || "").trim().toUpperCase();
const isTipoReflex = (tipo) => normalizarTipo(tipo).startsWith("REFLEX");
const isTipoCampanha = (tipo) => normalizarTipo(tipo).startsWith("CAMPANHA");


const formatUnidade = (value, unit, options = {}) => {
  if (value === null || value === undefined) return "-";

  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  const numValue = Number(value);

  if (isNaN(numValue)) return "-";

  const formattedValue = numValue.toLocaleString("pt-BR", {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  const normalizedUnit = String(unit || "").toLowerCase().trim();

switch (normalizedUnit) {
  case "moeda":
    return `R$ ${formattedValue}`;

  case "toneladas":
    return `${formattedValue} t`;

  case "percentual": {
    const percentValue = normalizePercentualValue(numValue);

    const isInteger =
      Math.abs(percentValue - Math.round(percentValue)) < 0.0001;

    return `${percentValue.toLocaleString("pt-BR", {
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: isInteger ? 0 : 2,
    })}%`;
  }

  case "unidade": {
    const isInteger =
      Math.abs(numValue - Math.round(numValue)) < 0.0001;

    return numValue.toLocaleString("pt-BR", {
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: isInteger ? 0 : 2,
    });
  }


  default:
    return formattedValue;
}

};

const formatMetaVolumeValue = (value, medida) => {
  const normalizedUnit = String(medida || "").toLowerCase().trim();

  if (normalizedUnit === "unidade") {
    return formatUnidade(value, "unidade", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  if (normalizedUnit === "toneladas") {
    return formatUnidade(value, "toneladas", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (normalizedUnit === "moeda") {
    return formatUnidade(value, "moeda", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (normalizedUnit === "percentual") {
    return formatUnidade(value, "percentual");
  }

  return formatUnidade(value, medida);
};

const campanhaZerada = (item) => {
  try {
    if (!item) return false;
    if (!isTipoCampanha(item.tipo)) return false;

    const metas = item.metas || {};
    const metasValues = ["m100", "m300", "m500"].map((k) => Number(metas[k] || 0));
    const metasAllZero = metasValues.every((v) => !Number.isFinite(v) || v === 0);

    const volReal = Number(item?.volumes?.real || 0);
    const atingReal = Number(item?.atingimentos?.real || 0);

    return metasAllZero && volReal === 0 && atingReal === 0;
  } catch (e) {
    return false;
  }
};

const shouldShowPriceFields = (metaPreco) => {
  const numericMetaPreco = Number(metaPreco);
  return Number.isFinite(numericMetaPreco) && numericMetaPreco !== 0;
};

const Cards = ({ dataCards, vendedores, isHistorico, isMesAtual }) => {
  const navigate = useNavigate();
  const [itemsToShow, setItemsToShow] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [index, setIndex] = useState(3);

  const { parametroReflex } = useParametroReflex();

  const sortCards = (cards) => {
    if (!cards || cards.length === 0) return [];

    const gatilhos = cards.filter(
      (card) => isIndicadorGatilhoAtivo(card) && isTipoReflex(card.tipo)
    );

    const kpis = cards
      .filter((card) => !isIndicadorGatilhoAtivo(card) && isTipoReflex(card.tipo))
      .sort((a, b) => {
        const pesoA = Number(a.pesoPercentual) || 0;
        const pesoB = Number(b.pesoPercentual) || 0;
        return pesoB - pesoA;
      });

    const campanhas = cards.filter((card) => isTipoCampanha(card.tipo));

    return [...gatilhos, ...kpis, ...campanhas];
  };

  useEffect(() => {
    if (dataCards && dataCards.length > 0) {
      const sortedCards = sortCards(dataCards);
      const initialItems = sortedCards.slice(0, 3);
      setItemsToShow(initialItems);
      setHasMore(sortedCards.length > 3);
      setIndex(3);
    }
  }, [dataCards]);

  const fetchMoreData = () => {
    if (!dataCards || dataCards.length === 0) return;

    const sortedCards = sortCards(dataCards);

    if (itemsToShow.length >= sortedCards.length) {
      setHasMore(false);
      return;
    }

    setTimeout(() => {
      const nextItems = sortedCards.slice(0, index + 3);
      setItemsToShow(nextItems);
      setIndex((prevIndex) => prevIndex + 3);

      if (nextItems.length >= sortedCards.length) {
        setHasMore(false);
      }
    }, 500);
  };

  if (!dataCards || Object.keys(dataCards).length === 0) {
    return null;
  }

  const FIX_VENDOR_INDEX = 0;
  const FIX_REFLEX_INDEX = 0;
  const vendedorFixo = Mock?.[FIX_VENDOR_INDEX] ?? null;
  const mesFixo = vendedorFixo?.meses?.[(vendedorFixo?.meses?.length ?? 1) - 1] ?? null;
  const indicadorFixo = mesFixo?.reflex?.[FIX_REFLEX_INDEX] ?? null;




  return (
    <InfiniteScroll
      dataLength={itemsToShow.length}
      next={fetchMoreData}
      hasMore={hasMore}
      loader={
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      }
      endMessage={
        <div>

          <p className="flex justify-center mt-4">
          Atualizado em:{" "}
          <strong className="ml-2">
            {new Date(vendedores[0].createdAt).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </strong>
        </p>




        </div>
      }
    >
      <div className="flex flex-col gap-5 mt-8">
        {itemsToShow.map((item, key) => (
          <div key={key} className="relative rounded-lg p-1 pl-6" style={{ backgroundColor: getBackgroundColor(item) }}>
            {isTipoReflex(item.tipo) ? (
              (() => {
                const showPriceFields = shouldShowPriceFields(item?.metaPreco);

                return (
              <div className="cards-reflex bg-white rounded-lg">
                <div className="bg-white rounded-lg py-2 px-6 flex-1 w-full">
                  <div className="flex items-center justify-between">
                    <div className="text-[#334155]">
                      <p className="text-xs">INDICADOR</p>
                      <div className="flex gap-2 justify-center items-center">
                        <h2 className="font-bold text-2xl ">{item.kpi}</h2>
                        {(!atingiuMeta({
                          valor: item?.volumes?.realAjustado,
                          meta: item?.metas?.m100,
                          direcao: getDirecaoMetas(item?.metas),
                        })) && (
                          <div className=" w-[40px] flex justify-center items-center">
                            <IconAlert />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cards-sub-reflex gap-3 mt-6 ">
                    <div className={`flex flex-col justify-between  gap-4 ${showPriceFields ? "" : "mt-[68px]"}`}>
                      <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] desktop-reflex">
                        <p className="text-[#334155]">
                          Volume Real: {formatMetaVolumeValue(item?.volumes?.real, item.medida)}
                        </p>
                      </div>

                      {showPriceFields && (
                        <>
                          <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] desktop-reflex">
                            <p className="text-[#334155]">
                              Preço Real: {formatUnidade(item?.precoReal, "moeda")}
                            </p>
                          </div>

                          <div className="bg-[#CED4E2] p-2 rounded-lg shadow-sm border border-gray-200 desktop-reflex">
                            <p className="text-[#334155] px-[8px] py-[1px]">
                              Preço Meta: {formatUnidade(item.metaPreco, "moeda")}
                            </p>
                          </div>
                        </>
                      )}

                      <div className="flex justify-between gap-4 mobile-reflex">
                        {Object.entries(item.metas || {}).map(([key, value]) => {
                          if (key === "m700") return null;

                          const realAjustado = item.volumes?.real || 0;
                          const metaAtingida = getMetaAtingida({ valor: realAjustado, metas: item.metas });

                          const isAtivo = key === metaAtingida;
                          const formattedValue = formatMetaVolumeValue(value, item.medida);

                          return (
                            <button
                              key={key}
                              className={`  px-2 rounded-[8px] text-sm transition-colors block w-full mt-1 flex justify-center items-center
                        ${isAtivo ? "bg-primaria text-white  font-semibold" : "bg-white text-[#374151] border border-1   border-[#E1E7EF]"}
                      `}
                            >
                              <span className="text-base text-[14px]">{key.toUpperCase()}: <b className="text-[13px]">{formattedValue}</b></span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {showPriceFields && (
                      <div className="variation-card-reflex">
                        <div className="bg-[#F1F5F9] text-[#334155] text-[12px] max-w-[200px] max-h-[60px] rounded-[30px] flex flex-col items-center justify-center px-4 py-2">
                          <p>Variação: {fmtPct(item.variacaoPreco, { min: 0, max: 2 })}</p>
                          <p>Fator de Ajuste: {fmtPct(item.fatorAjuste, { min: 0, max: 2 })}</p>
                        </div>
                      </div>
                    )}

                    <div className="details-card-reflex flex flex-col gap-2">
                      <div className="">
                        {getBackgroundColor(item) === "#E4532E" && (
                          <div className="card-indicador-nome">
                            <p className="">Indicador Gatilho</p>
                          </div>
                        )}
                        <div className="flex justify-between gap-4 desktop-reflex">
                          {Object.entries(item.metas || {}).map(([key, value]) => {
                            if (key === "m700") return null;

                            const realAjustado = item.volumes?.real || 0;
                            const metaAtingida = getMetaAtingida({ valor: realAjustado, metas: item.metas });

                            const isAtivo = key === metaAtingida;
                            const formattedValue = formatMetaVolumeValue(value, item.medida);

                            return (
                              <button
                                key={key}
                                className={`flex-1 py-2 px-2 rounded-[8px] text-sm transition-colors
                        ${isAtivo ? "bg-[#F9FAFB] border border-black font-semibold" : "bg-[#F9FAFB] text-[#374151]"}
                      `}
                              >
                                {key.toUpperCase()} <br />
                                <span className="font-bold text-base">{formattedValue}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="mobile-reflex w-full">
                        {showPriceFields && (
                          <div className="bg-[#CED4E2] p-2 rounded-lg shadow-sm border border-gray-200 mobile-reflex mb-1 mobile">
                            <p className="text-[#334155] px-[8px] py-[1px]">
                              Preço Meta: {formatUnidade(item.metaPreco, "moeda")}
                            </p>
                          </div>
                        )}
                        <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] w-full mobile-reflex mb-1 mobile">
                          <p className="text-[#334155]">
                            Volume Real: {formatMetaVolumeValue(item?.volumes?.real, item.medida)}
                          </p>
                        </div>

                        {showPriceFields && (
                          <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] mobile-reflex mb-1 mobile">
                            <p className="text-[#334155]">
                              Preço Real: {formatUnidade(item?.precoReal, "moeda")}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="w-full">
                        {showPriceFields && (
                          <div className="mobile-reflex">
                            <div className="flex gap-2 mb-1">
                              <div className="bg-[#CED4E2] flex justify-start items-center px-4 py-2 rounded-lg  border-gray-200 w-full mobile">
                                <p className="text-[#334155] px-[8px] py-[1px]">
                                  Variação: {fmtPct(item.variacaoPreco, { min: 0, max: 2 })}
                                </p>
                              </div>
                              <div className="bg-[#CED4E2] flex justify-start items-center px-4 py-2 rounded-lg  border-gray-200 w-full mobile">
                                <p className="text-[#334155] px-[8px] py-[1px]">
                                  F. Ajuste: {fmtPct(item.fatorAjuste, { min: 0, max: 2 })}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {showPriceFields && (
                        <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] mb-1 mobile">
                          <p className="text-[#334155]">
                            Volume Real Ajustado: {formatMetaVolumeValue(item?.volumes?.realAjustado, item.medida)}
                          </p>
                        </div>
                        )}

                        <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF] mobile">
                          <p className="text-[#334155]">
                            Volume Projetado Ajustado: {formatMetaVolumeValue(item?.volumes?.projetadoAjustado, item.medida)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Speedometer pontuacao={item.atingimentos.projetado} indicador={item} parametroReflex={parametroReflex} isHistorico={isHistorico} isMesAtual={isMesAtual} />
                  </div>
                </div>
              </div>
                );
              })()
            ) : (
              <div className="bg-[#FEFEDD] grid gap-4 grid-cols-[1fr] lg:grid-cols-[1fr_1fr_1fr] rounded-lg py-4 px-6">
                <div className="campanha-card-reflex">
                  <p className="campanha-card-reflex_sub">Campanha</p>
                  <div className='campanha-card-reflex_title'>
                    {isTipoCampanha(item.tipo) && (
                      parametroReflex?.find(
                        (p) => String(p.kpi || "").toUpperCase() === String(item.kpi || "").toUpperCase()
                      )?.mensagem ? (
                        <TooltipReflex
                          message={
                            parametroReflex?.find(
                              (p) => String(p.kpi || "").toUpperCase() === String(item.kpi || "").toUpperCase()
                            )?.mensagem
                          }
                        >
                          <InfoFormReflex />
                        </TooltipReflex>
                      ) : null
                    )}
                    <h2 className="font-bold text-2xl text-[#000000]">{item.kpi}</h2>
                  </div>
                </div>
                <div>
                  <div className="flex flex-col gap-2 ">
                    <div className=" flex justify-between gap-4  ">
                      {Object.entries(item.metas || {}).map(([key, value]) => {
                        if (key === "m700") return null;

                        const realAjustado = item.volumes?.real || 0;
                          const metaAtingida = getMetaAtingida({ valor: realAjustado, metas: item.metas });

                          const isAtivo = key === metaAtingida;
                          const formattedValue = formatMetaVolumeValue(value, item.medida);

                        return (
                          <button
                            key={key}
                            className={`flex-1 py-2 px-2 rounded-[8px] text-sm ${
                              isAtivo
                                ? "bg-[#F9FAFB] text-[#374151] border border-[#000000] font-bold"
                                : "bg-[#F9FAFB] text-[#374151]"
                            }`}
                          >
                            {key.toUpperCase()}
                            <br />
                            <span className="font-bold text-base">{formattedValue}</span>
                          </button>
                        );
                      })}
                    </div>
                        <div className="bg-white px-4 py-2 rounded-lg border-2 border-[#E1E7EF]">
                          <p className="text-[#334155]">
                            Realizado: {formatMetaVolumeValue(item.volumes.real, item.medida)}
                          </p>
                        </div>
                  </div>
                </div>
                    <div>
                      <p className="h-[100%] ">
                        <p className="bg-[#F5C115] h-[100%] rounded flex justify-center items-center font-bold text-3xl text-[#000000]">
                          {(item.atingimentos?.real ?? '0')} Pontos
                        </p>
                      </p>
                    </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </InfiniteScroll>
  );
};

Cards.propTypes = {
  dataCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tipo: PropTypes.string,
      gatilho: PropTypes.bool,
      kpi: PropTypes.string,
      medida: PropTypes.string, // Adicionado propTypes para medida
      pesoPercentual: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      precoReal: PropTypes.number,
      metaPreco: PropTypes.number,
      fatorAjuste: PropTypes.number,
      variacaoPreco: PropTypes.number,
      metas: PropTypes.objectOf(PropTypes.number),
      volumes: PropTypes.shape({
        real: PropTypes.number,
        projetado: PropTypes.number,
        realAjustado: PropTypes.number,
        projetadoAjustado: PropTypes.number,
      }),
      atingimentos: PropTypes.shape({
        projetado: PropTypes.number,
      }),
    })
  ),
  vendedores: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        createdAt: PropTypes.string,
      })
    ),
    PropTypes.shape({
      createdAt: PropTypes.string,
    }),
  ]),
};

Cards.defaultProps = {
  dataCards: [],
};

export default Cards;